import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import Button from "@components/button";
import { Store } from "@apis/index";
import { useModal } from "@context/ModalContext";
import { useNavigate } from "react-router-dom";
import Typography from "@components/typography";
import Grid from "@components/grid";
import useConfig from "@hooks/useConfig";
import styled from "@emotion/styled";
import theme from "@utils/theme";
import Fields from "@components/fields";
import {
  Store as StoreIcon,
  MapPin,
  Search,
  Filter,
  X,
  CheckCircle,
  Sparkles,
} from "lucide-react";

const ModalContainer = styled.div`
  padding: 0;
  width: 100%;
  height: 85vh;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.background};
  border-radius: 16px;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 24px 32px;
  background: linear-gradient(
    135deg,
    ${theme.colors.white} 0%,
    ${theme.colors.brown50} 100%
  );
  border-bottom: 1px solid ${theme.colors.borderColor};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${theme.orange.orange50};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.orange.orange600};
`;

const SearchSection = styled.div`
  width: 400px;
  position: relative;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textLight};
  width: 20px;
  height: 20px;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.borderColor};
  border-radius: 8px;
  color: ${theme.colors.textGrey};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.orange.orange400};
    color: ${theme.orange.orange600};
  }
`;

const StatsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 12px 0;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${theme.colors.textGrey};
  font-size: 14px;

  strong {
    color: ${theme.colors.textPrimary};
    font-weight: 600;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 32px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${theme.colors.brown50};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.borderColor};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.textLight};
  }
`;

const StoreCard = styled.div<{ selected: boolean }>`
  border: 2px solid
    ${({ selected }) =>
      selected ? theme.orange.orange500 : theme.colors.borderColor};
  border-radius: 16px;
  cursor: pointer;
  overflow: hidden;
  background: ${theme.colors.white};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 280px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  width: 100%;
  &:hover {
    border-color: ${theme.orange.orange500};
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      ${theme.orange.orange500} 0%,
      ${theme.orange.orange300} 100%
    );
    opacity: ${({ selected }) => (selected ? 1 : 0)};
    transition: opacity 0.3s ease;
  }
`;

const StoreImage = styled.div<{ src: string }>`
  width: 100%;
  height: 160px;
  background-image: url(${(props) => props.src});
  background-size: cover;
  background-position: center;
  background-color: ${theme.colors.brown50};
  border-bottom: 1px solid ${theme.colors.borderColor};
  position: relative;
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, transparent 60%, rgba(0, 0, 0, 0.1));
`;

const VerifiedBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${theme.colors.success};
  color: white;
  padding: 4px 8px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const InitialsPlaceholder = styled.div`
  width: 100%;
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${theme.orange.orange50} 0%,
    ${theme.colors.white} 100%
  );
  color: ${theme.orange.orange600};
  font-size: 48px;
  font-weight: 700;
  border-bottom: 1px solid ${theme.colors.borderColor};
  text-transform: uppercase;
  position: relative;
`;

const StoreInfo = styled.div`
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StoreName = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  margin-bottom: 4px;
`;

const Location = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${theme.colors.textGrey};
  font-size: 13px;
  margin-bottom: 8px;
`;

const ModuleBadge = styled.div`
  background: ${theme.orange.orange50};
  color: ${theme.orange.orange700};
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-start;
  margin-top: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 20px;
`;

const EmptyStateIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${theme.colors.brown50};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.textLight};
`;

const ModalFooter = styled.div`
  padding: 20px 32px;
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.borderColor};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
`;

const SelectionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${theme.colors.textGrey};
  font-size: 14px;
`;

const StoreSelectionModal = ({
  moduleId,
  moduleName,
}: {
  moduleId: any;
  moduleName: string;
}) => {
  const [stores, setStores] = useState<any[]>([]);
  const { hideModal } = useModal();
  const navigate = useNavigate();
  const { bucketUrl } = useConfig();
  const [search, setSearch] = useState("");
  const [selectedStore, setSelectedStore] = useState<any>(null);

  useEffect(() => {
    Store.list({
      state: "allStores",
      params: {
        moduleId,
        limit: 1000,
        status: "Verified",
      },
    }).then((res: any) => {
      const items = res?.items || res?.data?.items || res?.data || res || [];
      if (Array.isArray(items)) {
        setStores(items);
      } else {
        console.error("Unexpected store response format:", res);
        setStores([]);
      }
    });
  }, [moduleId]);

  const filteredStores = stores.filter((s) => {
    const matchesModule = moduleId ? s.module?.id == moduleId : true;
    const searchLower = search.toLowerCase();
    return (
      matchesModule &&
      (s.storeName?.toLowerCase().includes(searchLower) ||
        s.name?.toLowerCase().includes(searchLower))
    );
  });

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("");
  };

  const handleStoreSelect = (store: any) => {
    setSelectedStore(store);
    setTimeout(() => {
      hideModal();
      navigate(
        `/admin/users/store/create-product/${store.id}?module=${moduleName.toLowerCase()}`,
        { state: store }
      );
    }, 300);
  };

  return (
    <Formik initialValues={{ store: "", search: "" }} onSubmit={() => {}}>
      {({ values }) => (
        <ModalContainer>
          <ModalHeader>
            <HeaderTop>
              <TitleSection>
                <IconWrapper>
                  <StoreIcon size={24} />
                </IconWrapper>
                <div>
                  <Typography weight={700} size={24}>
                    Select Store
                  </Typography>
                  <Typography size={14} color={theme.colors.textGrey}>
                    Choose a {moduleName} store to add a product
                  </Typography>
                </div>
              </TitleSection>

              <SearchSection>
                <SearchIcon />
                <Fields.Input
                  name="search"
                  label=""
                  placeholder="Search stores by name..."
                  value={search}
                  onChange={(val: any) => setSearch(val)}
                  styleInput="padding: 12px 16px 12px 48px; border-radius: 12px; font-size: 14px; border: 1px solid #E5E7EB;"
                />
              </SearchSection>
            </HeaderTop>

            <StatsBar>
              <StatItem>
                <strong>{filteredStores.length}</strong> stores available
              </StatItem>
              <StatItem>
                <CheckCircle size={16} />
                All stores are verified
              </StatItem>
              <StatItem>
                <Sparkles size={16} />
                Ready for product creation
              </StatItem>
            </StatsBar>
          </ModalHeader>

          <ScrollableContent>
            {filteredStores.length === 0 ? (
              <EmptyState>
                <EmptyStateIcon>
                  <StoreIcon size={40} />
                </EmptyStateIcon>
                <div>
                  <Typography
                    size={20}
                    weight={600}
                    styleText="margin-bottom: 8px;"
                  >
                    No stores found
                  </Typography>
                  <Typography size={14} color={theme.colors.textGrey}>
                    {search
                      ? `No stores match "${search}"`
                      : `No stores available in ${moduleName} module`}
                  </Typography>
                </div>
                {search && (
                  <Button
                    title="Clear search"
                    variant="outline"
                    onClick={() => setSearch("")}
                    icon={<X size={16} />}
                  />
                )}
              </EmptyState>
            ) : (
              <Grid
                repeat={3}
                stack={false}
                styleGrid="display: grid; grid-template-columns: repeat(auto-fill, minmax(1fr, 1fr)); gap: 24px;"
              >
                {filteredStores.map((store) => {
                  const displayImage =
                    store.coverImg ||
                    (store.coverImages && store.coverImages.length > 0
                      ? store.coverImages[0].coverImages
                      : undefined) ||
                    store.avatar;
                  const fullImageUrl = displayImage
                    ? displayImage.startsWith("http")
                      ? displayImage
                      : `${bucketUrl}/${displayImage}`
                    : null;

                  return (
                    <StoreCard
                      key={store.id}
                      selected={selectedStore?.id === store.id}
                      onClick={() => handleStoreSelect(store)}
                    >
                      {fullImageUrl ? (
                        <StoreImage src={fullImageUrl}>
                          <ImageOverlay />
                          <VerifiedBadge>
                            <CheckCircle size={12} />
                            Verified
                          </VerifiedBadge>
                        </StoreImage>
                      ) : (
                        <InitialsPlaceholder>
                          {getInitials(store.storeName || store.name || "ST")}
                          <VerifiedBadge>
                            <CheckCircle size={12} />
                            Verified
                          </VerifiedBadge>
                        </InitialsPlaceholder>
                      )}

                      <StoreInfo>
                        <StoreName weight={700} size={16}>
                          {store.storeName || store.name}
                        </StoreName>

                        <Location>
                          <MapPin size={14} />
                          <Typography size={13} color={theme.colors.textGrey}>
                            {store.city ||
                              store.zone?.name ||
                              "Location not specified"}
                          </Typography>
                        </Location>

                        <ModuleBadge>
                          {store.module?.name || moduleName}
                        </ModuleBadge>
                      </StoreInfo>
                    </StoreCard>
                  );
                })}
              </Grid>
            )}
          </ScrollableContent>

          <ModalFooter>
            <SelectionInfo>
              {selectedStore && (
                <>
                  <CheckCircle size={16} color={theme.colors.success} />
                  <span>
                    Selected:{" "}
                    <strong>
                      {selectedStore.storeName || selectedStore.name}
                    </strong>
                  </span>
                </>
              )}
            </SelectionInfo>

            <div style={{ display: "flex", gap: "12px" }}>
              <Button title="Cancel" variant="outline" onClick={hideModal} />
              {selectedStore && (
                <Button
                  title="Continue"
                  variant="primary"
                  onClick={() => handleStoreSelect(selectedStore)}
                />
              )}
            </div>
          </ModalFooter>
        </ModalContainer>
      )}
    </Formik>
  );
};

export default StoreSelectionModal;
